import React, { useState, useEffect } from 'react';
import { CheckSquare, Plus, CheckCircle2, Circle, Clock, Loader2, Trash2 } from 'lucide-react';

export default function AdminTasks() {
  const [tasks, setTasks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [newTaskTitle, setNewTaskTitle] = useState('');
  const [newTaskPriority, setNewTaskPriority] = useState('Mittel');

  useEffect(() => { fetchTasks(); }, []);

  const fetchTasks = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch('/api/admin/tasks', { headers: { 'Authorization': `Bearer ${token}` } });
      if (response.ok) setTasks(await response.json());
    } catch (err) { console.error(err); } finally { setIsLoading(false); }
  };

  const handleCreateTask = async (e) => {
    e.preventDefault();
    if (!newTaskTitle.trim()) return;
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch('/api/admin/tasks', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ title: newTaskTitle, priority: newTaskPriority })
      });
      if (response.ok) {
        setNewTaskTitle('');
        fetchTasks();
      }
    } catch (err) { console.error(err); }
  };

  const toggleStatus = async (taskId, currentStatus) => {
    const newStatus = currentStatus === 'pending' ? 'completed' : 'pending';
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`/api/admin/tasks/${taskId}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ status: newStatus })
      });
      if (response.ok) setTasks(tasks.map(t => t.id === taskId ? { ...t, status: newStatus } : t));
    } catch (err) { console.error(err); }
  };

  const handleDelete = async (taskId) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`/api/admin/tasks/${taskId}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
      if (response.ok) setTasks(tasks.filter(t => t.id !== taskId));
    } catch (err) { console.error(err); }
  };

  return (
    <div className="animate-in fade-in slide-in-from-bottom-4">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-primary">Aufgabenverwaltung</h1>
        <p className="text-muted-foreground">Verwalten Sie Ihre internen To-dos und Team-Aufgaben.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Neue Aufgabe anlegen */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-xl border border-border shadow-sm p-6 sticky top-8">
            <h2 className="font-bold text-primary mb-4 flex items-center gap-2"><Plus className="w-5 h-5 text-accent" /> Neue Aufgabe</h2>
            <form onSubmit={handleCreateTask} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Aufgaben-Titel</label>
                <input type="text" required value={newTaskTitle} onChange={(e) => setNewTaskTitle(e.target.value)} placeholder="z.B. Server-Logs prüfen" className="w-full p-2.5 border border-border rounded outline-none focus:border-accent" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Priorität</label>
                <select value={newTaskPriority} onChange={(e) => setNewTaskPriority(e.target.value)} className="w-full p-2.5 border border-border rounded outline-none focus:border-accent bg-white">
                  <option value="Hoch">Hoch</option>
                  <option value="Mittel">Mittel</option>
                  <option value="Niedrig">Niedrig</option>
                </select>
              </div>
              <button type="submit" className="w-full bg-primary text-white py-2.5 rounded font-medium hover:bg-secondary transition-colors">Hinzufügen</button>
            </form>
          </div>
        </div>

        {/* Aufgabenliste */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-xl border border-border shadow-sm overflow-hidden min-h-[400px]">
            <div className="px-6 py-4 border-b border-border bg-slate-50/50 flex justify-between items-center">
              <h2 className="font-semibold text-primary">Ihre To-do Liste</h2>
            </div>
            
            {isLoading ? (
              <div className="flex justify-center items-center h-64"><Loader2 className="w-8 h-8 animate-spin text-accent" /></div>
            ) : tasks.length === 0 ? (
              <div className="flex justify-center items-center h-64 text-muted-foreground">Keine Aufgaben vorhanden.</div>
            ) : (
              <div className="divide-y divide-border">
                {tasks.map(task => (
                  <div key={task.id} className={`p-4 flex items-center justify-between hover:bg-slate-50 transition-colors ${task.status === 'completed' ? 'opacity-60' : ''}`}>
                    <div className="flex items-center gap-4">
                      <button onClick={() => toggleStatus(task.id, task.status)} className="text-slate-400 hover:text-accent transition-colors">
                        {task.status === 'completed' ? <CheckCircle2 className="w-6 h-6 text-green-500" /> : <Circle className="w-6 h-6" />}
                      </button>
                      <div>
                        <h3 className={`font-medium ${task.status === 'completed' ? 'line-through text-muted-foreground' : 'text-primary'}`}>{task.title}</h3>
                        <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {task.date}</span>
                          <span className={`px-2 py-0.5 rounded-full font-medium ${task.priority === 'Hoch' ? 'bg-red-100 text-red-700' : task.priority === 'Mittel' ? 'bg-yellow-100 text-yellow-700' : 'bg-blue-100 text-blue-700'}`}>
                            {task.priority}
                          </span>
                        </div>
                      </div>
                    </div>
                    <button onClick={() => handleDelete(task.id)} className="p-2 text-red-500 hover:bg-red-50 rounded" title="Löschen"><Trash2 className="w-5 h-5" /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}